// contentFeedApi.js — Apollo client for the Content Feed AppSync API.
// Separate endpoint from libraryCardApi (different CDK stack / AppSync API).
import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import {
  GET_CONTENT_FEED_QUERY,
  SEARCH_CONTENT_FEED_QUERY,
  REFRESH_CONTENT_FEED_MUTATION,
} from "../queries/contentFeedQueries";
import { getAuthToken, clearAuthState } from "../utils/authUtils";

const CONTENT_FEED_GRAPHQL_ENDPOINT =
  "https://xqvcg5jvpzchbcnklizjxuozqy.appsync-api.us-east-1.amazonaws.com/graphql";

const httpLink = createHttpLink({ uri: CONTENT_FEED_GRAPHQL_ENDPOINT });

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: token } : {}),
    },
  };
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(`contentFeedApi - GraphQL error: ${message}`, { locations, path });
    });
  }
  if (networkError?.statusCode === 401) {
    console.error("contentFeedApi - 401 Unauthorized, clearing auth state");
    clearAuthState();
    if (window.location.pathname !== "/login") {
      window.location.href = "/login";
    }
  }
});

const client = new ApolloClient({
  link: errorLink.concat(authLink).concat(httpLink),
  cache: new InMemoryCache(),
});

export const getContentFeedFromApi = async (input) => {
  const response = await client.query({
    query: GET_CONTENT_FEED_QUERY,
    variables: { input },
    fetchPolicy: "network-only",
  });
  return response?.data?.getContentFeed;
};

export const searchContentFeedFromApi = async (input) => {
  const response = await client.query({
    query: SEARCH_CONTENT_FEED_QUERY,
    variables: { input },
    fetchPolicy: "network-only",
  });
  return response?.data?.searchContentFeed;
};

export const refreshContentFeedFromApi = async (input) => {
  const response = await client.mutate({
    mutation: REFRESH_CONTENT_FEED_MUTATION,
    variables: { input },
  });
  const result = response?.data?.refreshContentFeed;
  if (!result) throw new Error("refreshContentFeed returned no data — check AppSync resolver logs");
  return result;
};
